// src/business/services/businessErpService.js
import { request, ROUTES } from "../../services/api";

/* ── Products (for dropdowns) ───────────────────────────── */
export const productsAPI = {
  list:    (params) => request({ url: ROUTES.businessProducts, method: "GET", params }),
  getById: (id)     => request({ url: ROUTES.businessProductById(id), method: "GET" }),
};

/* ── Suppliers ──────────────────────────────────────────── */
export const suppliersAPI = {
  list:    (params) => request({ url: ROUTES.businessSuppliers, method: "GET", params }),
  getById: (id)     => request({ url: ROUTES.businessSupplierById(id), method: "GET" }),
  update:  (id, data) => request({ url: ROUTES.businessSupplierById(id), method: "PUT", data }),
  remove:  (id)     => request({ url: ROUTES.businessSupplierById(id), method: "DELETE" }),
  getProducts:   (id) => request({ url: ROUTES.businessSupplierProducts(id), method: "GET" }),
  linkProduct:   (id, data) => request({ url: ROUTES.businessSupplierLinkProduct(id), method: "POST", data }),
  unlinkProduct: (id, productId) => request({ url: ROUTES.businessSupplierUnlinkProduct(id, productId), method: "DELETE" }),
  addByCode: (code) => request({ url: ROUTES.businessSupplierByCode, method: "POST", data: { code } }),
  getProductSuppliers: (productId) => request({ url: ROUTES.businessProductSuppliers(productId), method: "GET" }),
};

/* ── Procurement ────────────────────────────────────────── */
export const procurementAPI = {
  // Purchase Requisitions
  listPRs:   (params) => request({ url: ROUTES.businessPRs, method: "GET", params }),
  createPR:  (data)   => request({ url: ROUTES.businessPRs, method: "POST", data }),
  approvePR: (id)     => request({ url: ROUTES.businessPRApprove(id), method: "POST" }),
  cancelPR:  (id)     => request({ url: ROUTES.businessPRCancel(id), method: "POST" }),

  // RFQs
  listRFQs:      (params) => request({ url: ROUTES.businessRFQs, method: "GET", params }),
  createRFQ:     (data)   => request({ url: ROUTES.businessRFQs, method: "POST", data }),
  getRFQ:        (rfqId)  => request({ url: ROUTES.businessRFQById(rfqId), method: "GET" }),
  listQuotes:    (rfqId)  => request({ url: ROUTES.businessRFQQuotes(rfqId), method: "GET" }),
  inviteSupplier:(rfqId, supplierId) => request({ url: ROUTES.businessRFQInvite(rfqId), method: "POST", data: { supplierId } }),
  submitQuote:   (rfqId, data) => request({ url: ROUTES.businessRFQQuotes(rfqId), method: "POST", data }),
  acceptQuote:   (rfqId, quoteId) => request({ url: ROUTES.businessRFQAcceptQuote(rfqId, quoteId), method: "POST" }),

  // Purchase Orders
  listPOs:      (params) => request({ url: ROUTES.businessPOs, method: "GET", params }),
  createDirectPO:(data)  => request({ url: ROUTES.businessPOs, method: "POST", data }),
  getPO:        (id)     => request({ url: ROUTES.businessPOById(id), method: "GET" }),
  sendPO:       (id)     => request({ url: ROUTES.businessPOSend(id), method: "POST" }),
  openWork:     (params) => request({ url: ROUTES.businessProcurementOpenWork, method: "GET", params }),

  // Timeline
  timeline: (params) => request({ url: ROUTES.businessProcurementTimeline, method: "GET", params }),
};

/* ── Inventory ──────────────────────────────────────────── */
export const inventoryAPI = {
  receiveStock:  (data)   => request({ url: ROUTES.businessInventoryReceive, method: "POST", data }),
  adjustStock:   (data)   => request({ url: ROUTES.businessInventoryAdjust, method: "POST", data }),
  listMovements: (params) => request({ url: ROUTES.businessInventoryMovements, method: "GET", params }),
  listReceipts:  (params) => request({ url: ROUTES.businessInventoryReceipts, method: "GET", params }),
  listStockLevels: (params) => request({ url: ROUTES.businessInventoryLevels, method: "GET", params }),
};

/* ── Finance ────────────────────────────────────────────── */
export const financeAPI = {
  getDashboard: () => request({ url: ROUTES.businessFinanceDashboard, method: "GET" }),
  listPending: () => request({ url: ROUTES.businessFinancePending, method: "GET" }),
  listPayables: (params = {}) => request({ url: ROUTES.businessFinancePayables, method: "GET", params }),
  listDisbursements: (params = {}) => request({ url: ROUTES.businessFinanceDisbursements, method: "GET", params }),
  listSalaryTemplates: (params = {}) => request({ url: ROUTES.businessFinanceSalaryTemplates, method: "GET", params }),
  createSalaryTemplate: (data) => request({ url: ROUTES.businessFinanceSalaryTemplates, method: "POST", data }),
  updateSalaryTemplate: (id, data) => request({ url: ROUTES.businessFinanceSalaryTemplateById(id), method: "PATCH", data }),
  deactivateSalaryTemplate: (id) => request({ url: ROUTES.businessFinanceSalaryTemplateById(id), method: "DELETE" }),
  // APP-HR-080: an inactive preset can be made available again, or deleted for good (nothing keeps a preset's id).
  restoreSalaryTemplate: (id) => request({ url: `${ROUTES.businessFinanceSalaryTemplateById(id)}/restore`, method: "POST" }),
  deleteSalaryTemplate: (id) => request({ url: `${ROUTES.businessFinanceSalaryTemplateById(id)}/permanent`, method: "DELETE" }),
  listReceivables: (params = {}) => request({ url: ROUTES.businessFinanceReceivables, method: "GET", params }),
  listCollections: (params = {}) => request({ url: ROUTES.businessFinanceCollections, method: "GET", params }),
  getPayroll: (params = {}) => request({ url: ROUTES.businessFinancePayroll, method: "GET", params }),
  reviewPayrollPeriod: (periodId, data = {}) => request({ url: ROUTES.businessFinancePayrollReviewApprove(periodId), method: "POST", data }),
  returnPayrollPeriod: (periodId, data = {}) => request({ url: ROUTES.businessFinancePayrollReturn(periodId), method: "POST", data }),
  settlePayrollPeriod: (periodId, data = {}) => request({ url: ROUTES.businessFinancePayrollSettle(periodId), method: "POST", data }),
  // FIN-2: per-employee cash salary payment.
  listPeriodSalaries: (periodId) => request({ url: ROUTES.businessFinancePayrollSalaries(periodId), method: "GET" }),
  // Report 54A (D1): how each employee's pay in a payroll was worked out (read only).
  listPayrollCalculations: (periodId) => request({ url: ROUTES.businessFinancePayrollCalculations(periodId), method: "GET" }),
  recordCashHandover: (recordId, data, idempotencyKey) => request({
    url: ROUTES.businessFinanceSalaryCashHandover(recordId), method: "POST", data, headers: { "Idempotency-Key": idempotencyKey },
  }),
  confirmSalaryOnBehalf: (recordId, data) => request({ url: ROUTES.businessFinanceSalaryConfirmOnBehalf(recordId), method: "POST", data }),
  getAnalytics: () => request({ url: ROUTES.businessFinanceAnalytics, method: "GET" }),
  releaseDisbursement: (data) => request({ url: ROUTES.businessFinanceDisbursementRelease, method: "POST", data }),
  recordCollection: (data) => request({ url: ROUTES.businessFinanceCollectionRecord, method: "POST", data }),
  approvePO:   (id, data = {}) => request({ url: ROUTES.businessFinanceApprove(id), method: "POST", data }),
  rejectPO:    (id, data = {}) => request({ url: ROUTES.businessFinanceReject(id), method: "POST", data }),
};

/* ── Logistics ──────────────────────────────────────────── */
export const logisticsAPI = {
  list:         (params) => request({ url: ROUTES.businessDeliveries, method: "GET", params }),
  getById:      (id)     => request({ url: ROUTES.businessDeliveryById(id), method: "GET" }),
};
